import React, {
  lazy,
  ReactNode,
  Suspense,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Navigate } from 'react-router';
import { RouteObject } from 'react-router-dom';
import {
  DebugForm,
  IS_DEV_OR_STAGING,
} from './components/account/userSettings/DebugForm';
import SettingsLayout from './components/account/userSettings/SettingsLayout';
import TeamSettings from './components/account/userSettings/TeamSettings';
import { UserSettings } from './components/account/userSettings/UserSettings';
import AdminGuard from './components/authentication/AdminGuard';
import AuthGuard from './components/authentication/AuthGuard';
import GuestGuard from './components/authentication/GuestGuard';
import ApplicationLayout from './components/dashboard/AppLayout';
import NewBorrowerForm from './components/dashboard/installers/NewBorrowerForm/NewBorrowerForm';
import NewBorrowerFormOld from './components/dashboard/installers/NewBorrowerForm/NewBorrowerFormOld';
import LoadingScreen from './components/LoadingScreen';
import Referral from './components/utility_api/Referral';
import {
  ACCOUNT_TYPES,
  DEBUG_SETTINGS_PATH,
  NEW_APPLICATION_PATH,
  USER_RECORD_TYPES,
} from './constants';
import InstallerApplicationSubmitted from './pages/InstallerApplicationSubmitted';
import TrainingVideoPage from './pages/resources/TrainingVideoPage';
import { useFeatureFlag } from './hooks';
import { RAILWAY_FEATURE_FLAGS } from './railwayFeatureFlags';

interface CustomRouteObject extends RouteObject {
  element: ReactNode;
}

const LOGIN_REDIRECT_PATH = '/authentication/login';

// eslint-disable-next-line react/display-name
const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// Temporary component to switch between the old and new borrower
// form until we deprecate the old one
const BorrowerFormSwitcher = () => {
  const { isActive } = useFeatureFlag();
  const [useV2, setUseV2] = useState<boolean | null>(null);
  const didFetchBorrowerFormFlagRef = useRef(false);

  useEffect(() => {
    if (didFetchBorrowerFormFlagRef.current) return;
    didFetchBorrowerFormFlagRef.current = true;

    (async () => {
      try {
        const active = await isActive(
          RAILWAY_FEATURE_FLAGS.borrowerFormV2,
          'Railway'
        );
        setUseV2(active);
      } catch (err) {
        console.error(
          `Failed to fetch "${RAILWAY_FEATURE_FLAGS.borrowerFormV2}" flag:`,
          err
        );
      }
    })();
  }, []);

  if (useV2 === null) return <LoadingScreen />;
  return useV2 ? <NewBorrowerForm /> : <NewBorrowerFormOld />;
};

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(
  lazy(() => import('./pages/authentication/PasswordRecovery'))
);
const PasswordReset = Loadable(
  lazy(() => import('./pages/authentication/PasswordReset'))
);
const Register = Loadable(
  lazy(() => import('./pages/authentication/Register'))
);
const VerifyCode = Loadable(
  lazy(() => import('./pages/authentication/VerifyCode'))
);
const CheckEmail = Loadable(
  lazy(() => import('./pages/authentication/CheckEmail'))
);
const VerificationRequired = Loadable(
  lazy(() => import('./pages/authentication/VerificationRequired'))
);
const MagicLinkHandler = Loadable(
  lazy(() => import('./components/authentication/MagicLinkHandler'))
);
const CheckEmailSignup = Loadable(
  lazy(() => import('./pages/authentication/CheckEmailSignup'))
);

// Borrower Loan pages

const LoanBrowse = Loadable(lazy(() => import('./pages/dashboard/LoanBrowse')));
const BorrowerDocuments = Loadable(
  lazy(() => import('./pages/dashboard/BorrowerDocuments'))
);
const ApplicationReviewPage = Loadable(
  lazy(() => import('./pages/dashboard/loan_detail/ApplicationReviewPage'))
);

const ApplicationWelcomePage = Loadable(
  lazy(() => import('./pages/dashboard/loan_detail/ApplicationWelcome'))
);
const ApplicationPage = Loadable(
  lazy(() => import('./pages/dashboard/loan_detail/ApplicationPage'))
);
const ApplicationLoader = Loadable(
  lazy(() => import('./pages/dashboard/loan_detail/ApplicationLoader'))
);

// Installer Pages

const BorrowerBrowse = Loadable(
  lazy(() => import('./pages/dashboard/BorrowerBrowse'))
);

const ApplicantDetailsPage = Loadable(
  lazy(() => import('./pages/dashboard/ApplicantDetailsPage'))
);

const ProjectDetails = Loadable(
  lazy(() => import('./pages/dashboard/ProjectDetails/ProjectDetails'))
);

const ResourcesLayout = Loadable(
  lazy(() => import('./pages/resources/ResourcePageLayout'))
);

const MarketingMaterials = Loadable(
  lazy(() => import('./pages/resources/MarketingMaterialsPage'))
);
const RateSheets = Loadable(
  lazy(() => import('./pages/resources/RateSheetsPage'))
);

const FaqLayout = Loadable(
  lazy(() => import('./pages/resources/FaqPageLayout'))
);
const FaqPage = Loadable(lazy(() => import('./pages/resources/FaqPage')));

// Change Order Components
const ChangeOrderLoader = Loadable(
  lazy(() => import('./pages/dashboard/ChangeOrder/ChangeOrderLoader'))
);

const ChangeOrder = Loadable(
  lazy(() => import('./pages/dashboard/ChangeOrder/ChangeOrder'))
);
// Pricing Calculator
const PriceMyProjectPage = Loadable(
  lazy(() => import('./pages/pricing-calculator/PriceMyProjectPage'))
);

const LoanDetailsPage = Loadable(
  lazy(() => import('./pages/pricing-calculator/LoanDetails'))
);

const commonRoutes = [
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ) as React.ReactNode,
      },
      {
        path: 'password-recovery',
        element: <PasswordRecovery />,
      },
      {
        path: 'reset-password',
        element: <PasswordReset />,
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ) as React.ReactNode,
      },
      {
        path: 'verify',
        element: <VerifyCode />,
      },
      {
        path: 'verification-required',
        element: (
          <AuthGuard>
            <VerificationRequired />
          </AuthGuard>
        ) as React.ReactNode,
      },
      {
        path: 'check-email',
        element: <CheckEmail />,
      },
      {
        path: 'check-email-signup',
        element: <CheckEmailSignup />,
      },
      {
        path: 'magic-link',
        element: <MagicLinkHandler />,
      },
      {
        path: '*',
        element: <Navigate to={LOGIN_REDIRECT_PATH} />,
      },
    ],
  },
  {
    element: (
      <AuthGuard>
        <ApplicationLayout />
      </AuthGuard>
    ) as React.ReactNode,
    children: [
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="profile" replace />,
          },
          {
            path: 'profile',
            element: <UserSettings />,
          },
          {
            path: 'team',
            element: (
              <AdminGuard>
                <TeamSettings />
              </AdminGuard>
            ),
          },
        ],
      },
    ],
  },
  ...[
    IS_DEV_OR_STAGING
      ? {
          path: DEBUG_SETTINGS_PATH,
          element: (
            <AuthGuard>
              <ApplicationLayout />
            </AuthGuard>
          ) as React.ReactNode,
          children: [
            {
              index: true,
              element: <DebugForm />,
            },
            {
              path: '*',
              element: <Navigate to={`/${DEBUG_SETTINGS_PATH}/`} />,
            },
          ],
        }
      : {},
  ],
  {
    path: 'partner',
    children: [
      {
        path: 'application-submitted',
        element: <InstallerApplicationSubmitted />,
      },
    ],
  },
  {
    path: 'utility-bill-authorization-referral',
    element: (
      <AuthGuard>
        <Referral />
      </AuthGuard>
    ) as React.ReactNode,
  },
  { path: 'internal_error', element: <Navigate to={LOGIN_REDIRECT_PATH} /> },
  { path: '*', element: <Navigate to={LOGIN_REDIRECT_PATH} /> },
  { index: true, element: <Navigate to={LOGIN_REDIRECT_PATH} /> },
];

const borrowerRoutes = [
  {
    path: 'applications',
    children: [
      {
        index: true,
        element: <LoanBrowse />,
      },
      {
        path: ':loanId/*',
        element: <ApplicationLoader />,
        children: [
          {
            index: true,
            element: <ApplicationPage />,
          },
          {
            path: 'review',
            element: <ApplicationReviewPage />,
          },
          {
            path: 'welcome',
            element: <ApplicationWelcomePage />,
          },
          {
            path: '*',
            element: <Navigate to={'..'} />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to={LOGIN_REDIRECT_PATH} />,
      },
    ],
  },
  {
    path: 'documents',
    children: [
      {
        path: '/',
        element: <BorrowerDocuments />,
      },
      {
        path: '*',
        element: <Navigate to={LOGIN_REDIRECT_PATH} />,
      },
    ],
  },
  {
    path: 'faq',
    element: <FaqLayout />,
    children: [
      {
        index: true,
        element: <FaqPage />,
      },
    ],
  },
  {
    index: true,
    element: <Navigate to={LOGIN_REDIRECT_PATH} />,
  },
  {
    path: '*',
    element: <Navigate to={LOGIN_REDIRECT_PATH} />,
  },
];

const installerRoutes = [
  {
    path: 'resources',
    element: <ResourcesLayout />,
    children: [
      {
        path: 'rate-sheets',
        element: <RateSheets />,
      },
      {
        path: 'marketing-materials',
        element: <MarketingMaterials />,
      },
      {
        path: 'training-videos',
        element: <TrainingVideoPage />,
      },
    ],
  },
  {
    path: 'price-a-project',
    children: [
      {
        index: true,
        element: <PriceMyProjectPage />,
      },
    ],
  },
  {
    path: 'loan-details',
    children: [
      {
        index: true,
        element: <LoanDetailsPage />,
      },
    ],
  },
  {
    path: 'borrowers',
    children: [
      {
        index: true,
        element: <BorrowerBrowse />,
      },
      {
        path: 'applications/:applicationId/details',
        element: <ApplicantDetailsPage />,
      },
      {
        path: NEW_APPLICATION_PATH,
        element: <BorrowerFormSwitcher />,
      },
      {
        path: '*',
        element: <Navigate to={LOGIN_REDIRECT_PATH} />,
      },
    ],
  },
  {
    path: 'projects/*',
    children: [
      {
        path: ':projectId',
        element: <ProjectDetails />,
      },
    ],
  },
  {
    path: 'change-orders',
    children: [
      {
        path: ':changeOrderId',
        element: <ChangeOrderLoader />,
        children: [
          {
            index: true,
            element: <ChangeOrder />,
          },
          {
            path: '*',
            element: <Navigate to={LOGIN_REDIRECT_PATH} />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={LOGIN_REDIRECT_PATH} />,
  },
  {
    index: true,
    element: <Navigate to={LOGIN_REDIRECT_PATH} />,
  },
];

export function getUserRoutes(user): CustomRouteObject[] {
  let dashboardRoutes;
  if (user?.record_type === USER_RECORD_TYPES.BORROWER) {
    dashboardRoutes = borrowerRoutes;
  } else if (
    [ACCOUNT_TYPES.INSTALLER, ACCOUNT_TYPES.CONSULTANT].includes(
      user?.account?.type
    )
  ) {
    dashboardRoutes = installerRoutes;
  } else {
    dashboardRoutes = [];
  }

  return [
    ...commonRoutes,
    {
      path: 'dashboard/*',
      element: (
        <AuthGuard>
          <ApplicationLayout />
        </AuthGuard>
      ) as React.ReactNode,
      children: dashboardRoutes,
    },
  ] as CustomRouteObject[];
}
